// @ts-nocheck
import React, { memo, useMemo, useCallback } from "react";
import ArrowLeft from "@icons/Arrow_Left";
import ArrowRight from "@icons/Arrow_Right";

/**
 * MintFooter Component - Rewritten for Performance & Clean Code
 * 
 * Features:
 * - Memoized calculations and components
 * - Proper button state management
 * - Accessible design with proper ARIA attributes
 * - Clean conditional rendering
 * - Performance optimized with React best practices
 */

const MintFooter = memo(({
  nextHover,
  nextButton,
  backButton,
  stepTotal,
  stepState,
  validateSelection,
  Previous,
  Continue,
  MintNft,
  webPrimaryColor
}) => {
  // Memoized validation state to prevent unnecessary re-renders
  const isSelectionValid = useMemo(() => {
    return validateSelection?.() ?? false;
  }, [validateSelection]);

  // Memoized step states
  const stepStates = useMemo(() => ({
    canGoBack: stepState > 0,
    canGoNext: stepState < stepTotal,
    canMint: stepState === stepTotal,
    isFirstStep: stepState === 0,
    isLastStep: stepState === stepTotal
  }), [stepState, stepTotal]);

  // Memoized button handlers to prevent function recreation
  const handlePrevious = useCallback(() => {
    if (stepStates.canGoBack && Previous) {
      Previous();
    }
  }, [stepStates.canGoBack, Previous]);

  const handleContinue = useCallback(() => {
    if (stepStates.canGoNext && isSelectionValid && Continue) {
      Continue();
    }
  }, [stepStates.canGoNext, isSelectionValid, Continue]);

  const handleMint = useCallback((e) => {
    if (stepStates.canMint && MintNft) {
      MintNft(e);
    }
  }, [stepStates.canMint, MintNft]);

  // Memoized Previous Button Component
  const PreviousButton = useMemo(() => {
    if (!stepStates.canGoBack) return null;

    return (
      <button
        className="mint__btn no-border mint__btn-prev"
        onClick={handlePrevious}
        aria-label={`Go back: ${backButton}`}
        type="button"
      >
        <div className="arrow-group left">
          <ArrowLeft color={webPrimaryColor} />
          <span>{backButton}</span>
        </div>
      </button>
    );
  }, [stepStates.canGoBack, handlePrevious, backButton, webPrimaryColor]);

  // Memoized Next Button Component
  const NextButton = useMemo(() => {
    if (!stepStates.canGoNext) return null;

    const arrowColor = isSelectionValid ? webPrimaryColor : "#cbcbcb";
    
    return (
      <button
        className={`mint__btn no-border mint__btn-next ${!isSelectionValid ? 'tooltip' : ''}`}
        onClick={handleContinue}
        disabled={!isSelectionValid}
        aria-label={isSelectionValid ? `Continue: ${nextButton}` : `Cannot continue: ${nextHover}`}
        type="button"
      >
        {!isSelectionValid && (
          <span className="tooltiptext" role="tooltip">
            {nextHover}
          </span>
        )}
        <div className="arrow-group right">
          <span>{nextButton}</span>
          <ArrowRight color={arrowColor} />
        </div>
      </button>
    );
  }, [
    stepStates.canGoNext,
    isSelectionValid,
    handleContinue,
    nextButton,
    nextHover,
    webPrimaryColor
  ]);

  // Memoized Mint Button Component
  const MintButton = useMemo(() => {
    if (!stepStates.canMint) return null;

    return (
      <button
        className="mint__btn no-border mint__btn-next mint__btn-mint"
        onClick={handleMint}
        aria-label="Mint your NFT"
        type="button"
      >
        <span className="mint-icon" aria-hidden="true"></span>
        <span>Mint!</span>
      </button>
    );
  }, [stepStates.canMint, handleMint]);

  // Main render with stable structure
  return (
    <div className="mint_btn_wrapper" role="navigation" aria-label="Mint process navigation">
      {PreviousButton}
      {NextButton}
      {MintButton}
    </div>
  );
});

// Display name for debugging
MintFooter.displayName = 'MintFooter';

export default MintFooter;
